import React from 'react';
import { ArrowRight, Ship, Truck, Shield, DollarSign, MapPin, CheckCircle } from 'lucide-react';
import SEO from '../components/SEO';

const BoatRVStorage = () => {
  const benefits = [
    'Minutes from Richland Chambers Lake',
    'Wide drive aisles for easy maneuvering',
    'Secure perimeter with electronic gate',
    'Affordable rates',
    'Concrete driveways to every space — never get stuck',
    'No HOA restrictions'
  ];

  const idealFor = [
    { icon: <Ship className="h-8 w-8 text-green-600" />, text: 'Bass Boats & Jet Skis' },
    { icon: <Truck className="h-8 w-8 text-green-600" />, text: 'Work Trucks & Commercial Vans' },
    { icon: <Truck className="h-8 w-8 text-green-600" />, text: 'Camper Trailers & RVs' },
    { icon: <Truck className="h-8 w-8 text-green-600" />, text: 'Extra Household Vehicles' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Boat, RV & Vehicle Storage Near Richland Chambers Lake | V&D Storage"
        description="Secure boat, RV, and vehicle storage on FM 416, minutes from Richland Chambers Lake. Concrete driveways to every space. No HOA restrictions."
        canonical="/boat-rv-storage"
      />
      {/* Header */}
      <section className="bg-green-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="boat-rv-title">
            Boat, RV & Vehicle Storage Near Richland Chambers Lake
          </h1>
          <p className="text-xl text-green-100 max-w-3xl mx-auto">
            Secure, affordable outdoor parking right off FM 416 – minutes from Richland Chambers Lake
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 leading-relaxed mb-6">
              HOA won't let you park your boat in the driveway? Need a secure spot for your work truck?
              V&D Storage offers affordable outdoor parking right off FM 416 — with fully concrete driveways to every space for smooth, all-weather access every time you visit.
            </p>
          </div>
        </div>
      </section>

      {/* Why Park With Us */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Park With Us?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-md text-center">
              <MapPin className="h-12 w-12 text-green-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Minutes from the Lake</h3>
              <p className="text-gray-600">
                Grab your boat and be on the water at Richland Chambers Reservoir in under 15 minutes.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-md text-center">
              <Truck className="h-12 w-12 text-green-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Concrete Driveways to Every Space</h3>
              <p className="text-gray-600">
                Fully concrete driveways give you smooth, reliable access for even the largest trailers — in any weather.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-md text-center">
              <Shield className="h-12 w-12 text-green-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Secure Perimeter</h3>
              <p className="text-gray-600">
                Fully fenced with electronic gate access. Your vehicle is safe and secure.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-md text-center">
              <DollarSign className="h-12 w-12 text-green-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Affordable Rates</h3>
              <p className="text-gray-600">
                Significantly cheaper than marina storage or covered boat ports.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Ideal For */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Ideal For:
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {idealFor.map((item, index) => (
              <div key={index} className="bg-gray-50 p-6 rounded-lg border border-gray-200 text-center">
                <div className="flex justify-center mb-3">{item.icon}</div>
                <p className="text-gray-800 font-medium">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Benefits */}
      <section className="py-16 bg-green-50 border-t border-b border-green-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            Additional Benefits
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-center space-x-3">
                <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0" />
                <span className="text-lg text-gray-700">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Park Your Boat or RV?</h2>
          <p className="text-xl text-gray-300 mb-8">
            Check availability and reserve your parking space online today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://vdstoragestreetman.storageunitsoftware.com/pages/rent"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-8 py-3 text-lg font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors shadow-lg btn-primary"
              data-testid="check-availability-button"
            >
              Check Parking Availability
              <ArrowRight className="ml-2 h-5 w-5" />
            </a>
            <a
              href="/contact"
              className="inline-flex items-center px-8 py-3 text-lg font-semibold text-gray-900 bg-white rounded-lg hover:bg-gray-100 transition-colors shadow-lg"
              data-testid="contact-button"
            >
              Contact Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BoatRVStorage;